import './dynamic-calendar.scss';
import classNames from 'classnames';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { Typography } from '../../components/typography/typography';
import { DateTime } from 'luxon';
import { Location } from '../../clients/quipli-service/location/location';
import Icon from '../../components/icons/icon';
import { useScrollSnap } from './hooks/use-scroll-snap';

export interface DynamicCalendarColumn {
  id: string;
  label: string;
  columnLabel: ReactNode;
}

export interface DynamicCalendarItem<T> {
  title: string;
  startTime: DateTime;
  duration: number;
  columnId: string;
  children: ReactNode;
  draggable?: boolean;
  metadata?: T;
}

interface DynamicCalendarProps<T> {
  columns: DynamicCalendarColumn[];
  startTime?: DateTime;
  endTime?: DateTime;
  items: DynamicCalendarItem<T>[];
  handleDrop: (arg0: any, columnId: string, time: DateTime) => void;
  day: DateTime;
  columnLabel: string;
  location: Location | undefined;
  draggingElement?: T;
  isElementDragging?(arg0: T): boolean;
  legend: ReactNode[];
}

interface CardProps {
  title: string;
  startTime: DateTime;
  duration: number;
  style: any;
  children: ReactNode;
  draggable?: boolean;
  isElementDragging?: (arg0: any) => boolean;
  metadata?: any;
}

const CalendarCard: React.FC<CardProps> = ({
  title,
  startTime,
  duration,
  children,
  draggable = true,
  metadata,
  isElementDragging,
  ...props
}) => {
  return (
    <div
      className={classNames(
        'calendar-card',
        !draggable ? 'not-draggable' : '',
        metadata && isElementDragging
          ? isElementDragging(metadata)
            ? 'dragging'
            : 'im-not-dragging'
          : 'nobody-is-dragging',
      )}
      {...props}
    >
      {children}
    </div>
  );
};

export const DynamicCalendar = <T,>({
  columns,
  startTime,
  endTime,
  items,
  handleDrop,
  day,
  columnLabel,
  location,
  draggingElement,
  isElementDragging,
  legend,
}: DynamicCalendarProps<T>) => {
  // Default to 7am and 7pm if not provided
  const defaultStartTime = useMemo(() => startTime || day.set({ hour: 7, minute: 0 }), [startTime, day]);
  const defaultEndTime = useMemo(() => endTime || day.set({ hour: 19, minute: 0 }), [endTime, day]);
  const [currentHoveredCol, setCurrentHoveredCol] = useState<DynamicCalendarColumn | null>(null);
  const [currentHoveredTime, setCurrentHoveredTime] = useState<DateTime | null>(null);
  const [currentTimePosition, setCurrentTimePosition] = useState<number | null>(null);
  const [legendOpen, setLegendOpen] = useState(true);

  const functionalColumns: DynamicCalendarColumn[] = [{ id: '-', label: '', columnLabel: <></> }, ...columns];

  const HEADER_HEIGHT = 66; // Height of the header in pixels
  const INTERVAL_HEIGHT = 30; // Height of each 15-minute interval in pixels

  // Use the scroll snap hook
  const { scrollRefCallback } = useScrollSnap({
    startTime: defaultStartTime,
    intervalHeight: INTERVAL_HEIGHT,
    scrollTargetSelector: '.right-col',
  });

  useEffect(() => {
    const updateCurrentTimePosition = () => {
      if (!location) {
        return;
      }
      const now = DateTime.now().setZone(location.timezone);
      const currentHour = now.hour;
      const currentMinutes = now.minute;
      // Convert to decimal hours then to minutes for more precise positioning
      const currentTimeInHours = currentHour + currentMinutes / 60;
      const totalMinutes = currentTimeInHours * 60;
      const totalIntervals = totalMinutes / 15;
      const position = HEADER_HEIGHT + totalIntervals * INTERVAL_HEIGHT;

      if (position < HEADER_HEIGHT || position > HEADER_HEIGHT + 24 * 4 * INTERVAL_HEIGHT) return;

      setCurrentTimePosition(position);
    };

    updateCurrentTimePosition();
    const interval = setInterval(updateCurrentTimePosition, 60000);

    return () => clearInterval(interval);
  }, [location]);

  const clearDragState = () => {
    setCurrentHoveredCol(null);
    setCurrentHoveredTime(null);
  };

  useEffect(() => {
    clearDragState();
  }, [columns]);

  useEffect(() => {
    if (!draggingElement) {
      clearDragState();
    }
  }, [draggingElement]);

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>, columnId: string, time: DateTime) => {
    e.preventDefault();
    setCurrentHoveredCol(columns.find((c) => c.id === columnId) || null);
    setCurrentHoveredTime(time);
  };

  const handleDropWrapper = (e: React.DragEvent<HTMLDivElement>, columnId: string, time: DateTime) => {
    handleDrop(e, columnId, time);
    e.preventDefault();
    clearDragState();
  };

  const generateTimeSlots = (columnId: string): JSX.Element[] => {
    const timeSlots = [];
    for (let hour = 0; hour < 24; hour++) {
      for (const minute of [0, 15, 30, 45]) {
        const period = hour < 12 ? 'AM' : 'PM';
        const formattedHour = hour % 12 === 0 ? 12 : hour % 12;
        const time = `${formattedHour}${minute < 10 ? '' : `:${minute}`}${period}`;
        const newTime = day.set({ hour, minute });

        // Check if this time slot is within the highlighted region
        const currentTime = day.set({ hour, minute });
        const isHighlighted = currentTime >= defaultStartTime && currentTime < defaultEndTime;

        timeSlots.push(
          <div
            key={time}
            data-time={time}
            className={classNames(
              'time-slot',
              minute === 15 || minute === 45 ? 'dashed' : 'underlined',
              isHighlighted ? 'highlighted-region' : 'non-highlighted-region',
            )}
            onDragOver={(e) => handleDragOver(e, columnId, newTime)}
            onDrop={(e) => handleDropWrapper(e, columnId, newTime)}
          >
            <div className="droppable-region"></div>
          </div>,
        );
      }
    }
    return timeSlots;
  };

  const calculateCardPosition = (startTimeItem: DateTime, duration: number) => {
    const startHour = startTimeItem.hour;
    const startMinute = startTimeItem.minute;
    const startMinutes = startHour * 60 + startMinute;
    const top = (startMinutes / (24 * 60)) * 100;
    const height = (duration / (24 * 60)) * 100;
    return { top: `${top}%`, minHeight: `${height}%` };
  };

  const itemsWithHovering = [
    ...items,
    ...(currentHoveredCol && currentHoveredTime
      ? [
          {
            columnId: currentHoveredCol.id,
            startTime: currentHoveredTime,
            duration: 60,
            children: <div className="hovering-content" />,
            draggable: false,
            title: '',
            metadata: undefined,
          },
        ]
      : []),
  ];

  const calculateOverlap = (columnId: string, startTime: DateTime, duration: number) => {
    const startHour = startTime.hour;
    const startMinute = startTime.minute;
    const startMinutes = startHour * 60 + startMinute;
    const endMinutes = startMinutes + duration;

    const overlappingItems = itemsWithHovering
      .filter((item) => !isElementDragging || !draggingElement || !item.metadata || !isElementDragging(item.metadata))
      .filter((item) => {
        if (item.columnId !== columnId) return false;

        const itemStartTime = item.startTime;
        const itemStartHour = itemStartTime.hour;
        const itemStartMinute = itemStartTime.minute;
        const itemStartMinutes = itemStartHour * 60 + itemStartMinute;
        const itemEndMinutes = itemStartMinutes + item.duration;

        const isOverlapping =
          (startMinutes < itemEndMinutes && endMinutes > itemStartMinutes) ||
          (itemStartMinutes < endMinutes && itemEndMinutes > startMinutes);

        return isOverlapping;
      });

    const overlapCount = overlappingItems.length;
    const overlapIndex = overlappingItems.findIndex(
      (item) => item.startTime === startTime && item.duration === duration,
    );

    return { overlapCount, overlapIndex };
  };

  if (!columns.length) {
    return <div className="no-cols">No {columnLabel}s selected, please adjust filters.</div>;
  }

  return (
    <div
      ref={scrollRefCallback}
      className="dispatch-calendar"
      style={{ '--columns': columns.length, '--functionalColumns': functionalColumns.length } as React.CSSProperties}
    >
      <div className="column-labels">
        {functionalColumns.map((column) => (
          <div key={column.id} className="column-label">
            {column.columnLabel}
          </div>
        ))}
      </div>
      <div className="time-labels">
        {generateTimeSlots('-').map((slot, index) => (
          <div
            key={index}
            className={classNames(
              'time-slot time-slot-label',
              (index + 1) % 4 === 0 ? 'underlined' : '',
              index === 0 ? 'top' : '',
            )}
          >
            <Typography type={'text-sm'} className="text-grey-500">
              {index % 4 === 0 && slot.props['data-time']}
            </Typography>
          </div>
        ))}
      </div>
      {columns.map((column) => (
        <div key={column.id} className="drop-zone-column" style={{ '--rows': 24 * 4 } as React.CSSProperties}>
          {generateTimeSlots(column.id)}
          {itemsWithHovering
            .filter((item) => item.columnId === column.id)
            .map((item, index) => {
              const { top, minHeight } = calculateCardPosition(item.startTime, item.duration);
              const { overlapCount, overlapIndex } = calculateOverlap(item.columnId, item.startTime, item.duration);
              return (
                <CalendarCard
                  key={index}
                  title={item.title}
                  startTime={item.startTime}
                  duration={item.duration}
                  draggable={item.draggable}
                  metadata={item.metadata}
                  isElementDragging={draggingElement ? isElementDragging : undefined}
                  style={{ top, minHeight, '--overlap-count': overlapCount, '--overlap-index': overlapIndex }}
                >
                  {item.children}
                </CalendarCard>
              );
            })}
        </div>
      ))}

      {currentTimePosition !== null && location != null && (
        <div className="current-time-indicator" style={{ top: `${currentTimePosition}px` }}>
          <div className="current-time-label">{DateTime.now().setZone(location.timezone).toFormat('h:mma')}</div>
        </div>
      )}

      {currentHoveredCol && currentHoveredTime && (
        <div className="hover-info">
          <div className="hover-body">
            <Typography type={'text-md'}>{`${currentHoveredCol.label} - ${currentHoveredTime.toFormat(
              'h:mma',
            )}`}</Typography>
          </div>
        </div>
      )}
      {legendOpen && (
        <div className="legend">
          <div className="legend-body">
            {legend.map((legendItem, index) => (
              <div key={index}>{legendItem}</div>
            ))}
            <div className="close-legend" onClick={() => setLegendOpen(false)}>
              <Icon icon="Close" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
